import * as __typia_transform__validateReport from "typia/lib/internal/_validateReport";
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { INTEGRATIONS_DOMAIN } from '../domain';
import typia from 'typia';
import { dispatchAction, type Action } from '@ardoq/rxbeach';
import { applyTableMapping, removeTargetWorkspace, setColumnMapping, } from 'integrations/common/streams/tabularMappings/actions';
import type { AiControlledCommand, CommandRejection, } from 'aiControlledCommands/types';
import { logWarn } from '@ardoq/logging';
import type { ColumnMapping } from 'integrations/common/streams/transferConfigs/types';
import type { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { MissingComponentsStrategy } from '@ardoq/api-types/integrations';
import { isReferenceFormatDisabled } from 'integrations/common/pages/tabularConfigMapping/utils';
import { resolvePayload } from './resolvePayload';
import type { AiReferenceFormat, AutomapColumnMapping, AutomapColumnsContext, AutomapColumnsPayload, ResolvedPayload, ResolvedTable, } from './types';
const toMissingComponents = (referenceFormat: AiReferenceFormat, canCreateComponents: boolean): {
    missingComponents?: MissingComponentsStrategy;
} => canCreateComponents &&
    !isReferenceFormatDisabled(referenceFormat, MissingComponentsStrategy.CREATE)
    ? { missingComponents: MissingComponentsStrategy.CREATE }
    : {};
export const toColumnMapping = ({ columnName, mappedTo }: AutomapColumnMapping, canCreateComponents: boolean): Partial<ColumnMapping> => {
    switch (mappedTo.columnType) {
        case 'component':
            return {
                columnType: 'component',
                hierarchyLevel: mappedTo.hierarchyLevel - 1,
                ...(mappedTo.componentType
                    ? { componentType: mappedTo.componentType.trim() }
                    : {}),
            };
        case 'component-path':
            return {
                columnType: 'component-path',
                componentTypes: mappedTo.componentTypes,
            };
        case 'field':
            if (!mappedTo.existingField) {
                return { columnType: 'field' };
            }
            return {
                columnType: 'field',
                fieldName: mappedTo.existingField.name,
                fieldLabel: mappedTo.existingField.label,
                fieldType: mappedTo.existingField.type,
            };
        case 'parent':
            return { columnType: 'parent', parentFormat: mappedTo.parentFormat };
        case 'reference':
            return {
                columnType: 'reference',
                referenceFormat: mappedTo.referenceFormat,
                referenceDirection: mappedTo.referenceDirection,
                referenceType: mappedTo.referenceType.trim(),
                targetWorkspace: mappedTo.targetWorkspace,
                componentType: mappedTo.componentType.trim(),
                ...toMissingComponents(mappedTo.referenceFormat, canCreateComponents),
            };
        case 'root-reference':
            return {
                columnType: 'root-reference',
                referenceFormat: mappedTo.referenceFormat,
                componentType: mappedTo.componentType.trim(),
                ...toMissingComponents(mappedTo.referenceFormat, canCreateComponents),
                ...(mappedTo.referenceType
                    ? { referenceType: mappedTo.referenceType.trim() }
                    : {}),
            };
        case 'target-reference':
            return {
                columnType: 'target-reference',
                referenceFormat: mappedTo.referenceFormat,
                componentType: mappedTo.componentType.trim(),
                ...toMissingComponents(mappedTo.referenceFormat, canCreateComponents),
            };
        case 'custom-id':
            if (!mappedTo.existingField) {
                return {
                    columnType: 'custom-id',
                    fieldName: null,
                    fieldLabel: columnName.trim(),
                };
            }
            return {
                columnType: 'custom-id',
                fieldName: mappedTo.existingField.name,
                fieldLabel: mappedTo.existingField.label,
            };
        case 'component-type':
        case 'ardoq-oid':
        case 'component-key':
        case 'description':
        case 'display-text':
        case 'tags':
        case 'reference-type':
            return { columnType: mappedTo.columnType };
    }
};
const toTableActions = (integrationId: IntegrationId, { id, name, index, sourceFieldNames, rowRepresentation, rootWorkspace, targetWorkspace, mappings, canCreateComponents, }: ResolvedTable): Action<unknown>[] => [
    removeTargetWorkspace({ integrationId, tableId: id }),
    applyTableMapping({
        integrationId,
        tableId: id,
        tableMapping: {
            name,
            index,
            rowRepresentation,
            rootWorkspace,
            mappedColumns: {},
            sourceFieldNames,
            ...(targetWorkspace ? { targetWorkspace } : {}),
        },
    }),
    ...mappings.map(mapping => setColumnMapping({
        integrationId,
        tableId: id,
        columnIndex: mapping.columnIndex,
        columnName: mapping.columnName,
        columnMapping: toColumnMapping(mapping, canCreateComponents),
    })),
];
const REJECTION_LOG_CODE = 'ai_automap_columns_rejected';
const MAX_SCHEMA_PROBLEMS = 15;
const toSchemaProblems = (errors: typia.IValidation.IError[]): string[] => errors
    .slice(0, MAX_SCHEMA_PROBLEMS)
    .map(({ path, expected }) => `${path}: expected ${expected}`);
const reportRejection = (reason: string, details: Record<string, unknown>): CommandRejection => {
    logWarn(new Error('AI automap columns rejected'), reason, {
        tags: { team: 'integrations' },
        code: REJECTION_LOG_CODE,
        fingerprint: [REJECTION_LOG_CODE],
        ...details,
    });
    return { status: 'rejected', reason };
};
const isAutomapColumnsContext = (context: unknown): context is AutomapColumnsContext => {
    const contextResult = (() => { const _iv1 = new Set(["aws-v3", "azure-v3", "excel-v3", "json-importer", "servicenow-v3", "it-pedia", "servicenow-guided-exporter", "signavio-exporter", "celonis-exporter", "ardoq", "ardoq-reference-data", "ardoq-sample-data", "sharepoint-files", "jira-software", "entra-id", "signavio", "celonis", "symbio", "import-builder", "azure-resources"]); const _iv10 = new Set(["custom-id", "ardoq-oid", "component-key", "component", "component-path", "component-type", "field", "description", "display-text", "tags", "parent", "reference", "root-reference", "target-reference", "reference-type"]); const _vv13 = new Set(["aws-v3", "azure-v3", "excel-v3", "json-importer", "servicenow-v3", "it-pedia", "servicenow-guided-exporter", "signavio-exporter", "celonis-exporter", "ardoq", "ardoq-reference-data", "ardoq-sample-data", "sharepoint-files", "jira-software", "entra-id", "signavio", "celonis", "symbio", "import-builder", "azure-resources"]); const _vv22 = new Set(["custom-id", "ardoq-oid", "component-key", "component", "component-path", "component-type", "field", "description", "display-text", "tags", "parent", "reference", "root-reference", "target-reference", "reference-type"]); const _io0 = (input: any): boolean => true === _iv1.has(input.integrationId) && (Array.isArray(input.tables) && input.tables.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem))) && (Array.isArray(input.workspaces) && input.workspaces.every((elem: any) => "object" === typeof elem && null !== elem && _io3(elem))) && "boolean" === typeof input.canCreateWorkspace && "boolean" === typeof input.canCreateTypes && (Array.isArray(input.fields) && input.fields.every((elem: any) => "object" === typeof elem && null !== elem && _io4(elem))); const _io1 = (input: any): boolean => "string" === typeof input.id && "string" === typeof input.name && "number" === typeof input.index && (Array.isArray(input.columns) && input.columns.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.sourceFieldNames) && input.sourceFieldNames.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.sampleRows) && input.sampleRows.every((elem: any) => Array.isArray(elem) && elem.every((elem: any) => null === elem || "string" === typeof elem || "number" === typeof elem || "boolean" === typeof elem))) && (null === input.rowRepresentation || "components" === input.rowRepresentation || "references" === input.rowRepresentation) && (null === input.lockedRowRepresentation || "components" === input.lockedRowRepresentation || "references" === input.lockedRowRepresentation) && (Array.isArray(input.lockedColumns) && input.lockedColumns.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))); const _io2 = (input: any): boolean => "number" === typeof input.columnIndex && true === _iv10.has(input.columnType); const _io3 = (input: any): boolean => "string" === typeof input.id && "string" === typeof input.name && (Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem)) && (Array.isArray(input.referenceTypes) && input.referenceTypes.every((elem: any) => "string" === typeof elem)); const _io4 = (input: any): boolean => "string" === typeof input.name && "string" === typeof input.label && "string" === typeof input.type; const _vo0 = (input: any, _path: string, _exceptionable: boolean = true): boolean => [true === _vv13.has(input.integrationId) || _report(_exceptionable, {
            path: _path + ".integrationId",
            expected: "(\"ardoq\" | \"ardoq-reference-data\" | \"ardoq-sample-data\" | \"aws-v3\" | \"azure-resources\" | \"azure-v3\" | \"celonis\" | \"celonis-exporter\" | \"entra-id\" | \"excel-v3\" | \"import-builder\" | \"it-pedia\" | \"jira-software\" | \"json-importer\" | \"servicenow-guided-exporter\" | \"servicenow-v3\" | \"sharepoint-files\" | \"signavio\" | \"signavio-exporter\" | \"symbio\")",
            value: input.integrationId
        }), (Array.isArray(input.tables) || _report(_exceptionable, {
            path: _path + ".tables",
            expected: "Array<ContextTable>",
            value: input.tables
        })) && input.tables.map((elem: any, _index14: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
            path: _path + ".tables[" + _index14 + "]",
            expected: "ContextTable",
            value: elem
        })) && _vo1(elem, _path + ".tables[" + _index14 + "]", true && _exceptionable) || _report(_exceptionable, {
            path: _path + ".tables[" + _index14 + "]",
            expected: "ContextTable",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".tables",
            expected: "Array<ContextTable>",
            value: input.tables
        }), (Array.isArray(input.workspaces) || _report(_exceptionable, {
            path: _path + ".workspaces",
            expected: "Array<ContextWorkspace>",
            value: input.workspaces
        })) && input.workspaces.map((elem: any, _index15: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
            path: _path + ".workspaces[" + _index15 + "]",
            expected: "ContextWorkspace",
            value: elem
        })) && _vo3(elem, _path + ".workspaces[" + _index15 + "]", true && _exceptionable) || _report(_exceptionable, {
            path: _path + ".workspaces[" + _index15 + "]",
            expected: "ContextWorkspace",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".workspaces",
            expected: "Array<ContextWorkspace>",
            value: input.workspaces
        }), "boolean" === typeof input.canCreateWorkspace || _report(_exceptionable, {
            path: _path + ".canCreateWorkspace",
            expected: "boolean",
            value: input.canCreateWorkspace
        }), "boolean" === typeof input.canCreateTypes || _report(_exceptionable, {
            path: _path + ".canCreateTypes",
            expected: "boolean",
            value: input.canCreateTypes
        }), (Array.isArray(input.fields) || _report(_exceptionable, {
            path: _path + ".fields",
            expected: "Array<ContextField>",
            value: input.fields
        })) && input.fields.map((elem: any, _index16: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
            path: _path + ".fields[" + _index16 + "]",
            expected: "ContextField",
            value: elem
        })) && _vo4(elem, _path + ".fields[" + _index16 + "]", true && _exceptionable) || _report(_exceptionable, {
            path: _path + ".fields[" + _index16 + "]",
            expected: "ContextField",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".fields",
            expected: "Array<ContextField>",
            value: input.fields
        })].every((flag: boolean) => flag); const _vo1 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.id || _report(_exceptionable, {
            path: _path + ".id",
            expected: "string",
            value: input.id
        }), "string" === typeof input.name || _report(_exceptionable, {
            path: _path + ".name",
            expected: "string",
            value: input.name
        }), "number" === typeof input.index || _report(_exceptionable, {
            path: _path + ".index",
            expected: "number",
            value: input.index
        }), (Array.isArray(input.columns) || _report(_exceptionable, {
            path: _path + ".columns",
            expected: "Array<string>",
            value: input.columns
        })) && input.columns.map((elem: any, _index17: number) => "string" === typeof elem || _report(_exceptionable, {
            path: _path + ".columns[" + _index17 + "]",
            expected: "string",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".columns",
            expected: "Array<string>",
            value: input.columns
        }), (Array.isArray(input.sourceFieldNames) || _report(_exceptionable, {
            path: _path + ".sourceFieldNames",
            expected: "Array<string>",
            value: input.sourceFieldNames
        })) && input.sourceFieldNames.map((elem: any, _index18: number) => "string" === typeof elem || _report(_exceptionable, {
            path: _path + ".sourceFieldNames[" + _index18 + "]",
            expected: "string",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".sourceFieldNames",
            expected: "Array<string>",
            value: input.sourceFieldNames
        }), (Array.isArray(input.sampleRows) || _report(_exceptionable, {
            path: _path + ".sampleRows",
            expected: "Array<Array<ResponseDataCell>>",
            value: input.sampleRows
        })) && input.sampleRows.map((elem: any, _index19: number) => (Array.isArray(elem) || _report(_exceptionable, {
            path: _path + ".sampleRows[" + _index19 + "]",
            expected: "Array<ResponseDataCell>",
            value: elem
        })) && elem.map((elem: any, _index20: number) => null === elem || "string" === typeof elem || "number" === typeof elem || "boolean" === typeof elem || _report(_exceptionable, {
            path: _path + ".sampleRows[" + _index19 + "][" + _index20 + "]",
            expected: "(boolean | null | number | string)",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".sampleRows[" + _index19 + "]",
            expected: "Array<ResponseDataCell>",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".sampleRows",
            expected: "Array<Array<ResponseDataCell>>",
            value: input.sampleRows
        }), null === input.rowRepresentation || "components" === input.rowRepresentation || "references" === input.rowRepresentation || _report(_exceptionable, {
            path: _path + ".rowRepresentation",
            expected: "(\"components\" | \"references\" | null)",
            value: input.rowRepresentation
        }), null === input.lockedRowRepresentation || "components" === input.lockedRowRepresentation || "references" === input.lockedRowRepresentation || _report(_exceptionable, {
            path: _path + ".lockedRowRepresentation",
            expected: "(\"components\" | \"references\" | null)",
            value: input.lockedRowRepresentation
        }), (Array.isArray(input.lockedColumns) || _report(_exceptionable, {
            path: _path + ".lockedColumns",
            expected: "Array<LockedColumn>",
            value: input.lockedColumns
        })) && input.lockedColumns.map((elem: any, _index21: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
            path: _path + ".lockedColumns[" + _index21 + "]",
            expected: "LockedColumn",
            value: elem
        })) && _vo2(elem, _path + ".lockedColumns[" + _index21 + "]", true && _exceptionable) || _report(_exceptionable, {
            path: _path + ".lockedColumns[" + _index21 + "]",
            expected: "LockedColumn",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".lockedColumns",
            expected: "Array<LockedColumn>",
            value: input.lockedColumns
        })].every((flag: boolean) => flag); const _vo2 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["number" === typeof input.columnIndex || _report(_exceptionable, {
            path: _path + ".columnIndex",
            expected: "number",
            value: input.columnIndex
        }), true === _vv22.has(input.columnType) || _report(_exceptionable, {
            path: _path + ".columnType",
            expected: "(\"ardoq-oid\" | \"component\" | \"component-key\" | \"component-path\" | \"component-type\" | \"custom-id\" | \"description\" | \"display-text\" | \"field\" | \"parent\" | \"reference\" | \"reference-type\" | \"root-reference\" | \"tags\" | \"target-reference\")",
            value: input.columnType
        })].every((flag: boolean) => flag); const _vo3 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.id || _report(_exceptionable, {
            path: _path + ".id",
            expected: "string",
            value: input.id
        }), "string" === typeof input.name || _report(_exceptionable, {
            path: _path + ".name",
            expected: "string",
            value: input.name
        }), (Array.isArray(input.componentTypes) || _report(_exceptionable, {
            path: _path + ".componentTypes",
            expected: "Array<string>",
            value: input.componentTypes
        })) && input.componentTypes.map((elem: any, _index23: number) => "string" === typeof elem || _report(_exceptionable, {
            path: _path + ".componentTypes[" + _index23 + "]",
            expected: "string",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".componentTypes",
            expected: "Array<string>",
            value: input.componentTypes
        }), (Array.isArray(input.referenceTypes) || _report(_exceptionable, {
            path: _path + ".referenceTypes",
            expected: "Array<string>",
            value: input.referenceTypes
        })) && input.referenceTypes.map((elem: any, _index24: number) => "string" === typeof elem || _report(_exceptionable, {
            path: _path + ".referenceTypes[" + _index24 + "]",
            expected: "string",
            value: elem
        })).every((flag: boolean) => flag) || _report(_exceptionable, {
            path: _path + ".referenceTypes",
            expected: "Array<string>",
            value: input.referenceTypes
        })].every((flag: boolean) => flag); const _vo4 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.name || _report(_exceptionable, {
            path: _path + ".name",
            expected: "string",
            value: input.name
        }), "string" === typeof input.label || _report(_exceptionable, {
            path: _path + ".label",
            expected: "string",
            value: input.label
        }), "string" === typeof input.type || _report(_exceptionable, {
            path: _path + ".type",
            expected: "string",
            value: input.type
        })].every((flag: boolean) => flag); const __is = (input: any): input is AutomapColumnsContext => "object" === typeof input && null !== input && _io0(input); let errors: any; let _report: any; return (input: any): import("typia").IValidation<AutomapColumnsContext> => {
        if (false === __is(input)) {
            errors = [];
            _report = (__typia_transform__validateReport._validateReport as any)(errors);
            ((input: any, _path: string, _exceptionable: boolean = true) => ("object" === typeof input && null !== input || _report(true, {
                path: _path + "",
                expected: "AutomapColumnsContext",
                value: input
            })) && _vo0(input, _path + "", true) || _report(true, {
                path: _path + "",
                expected: "AutomapColumnsContext",
                value: input
            }))(input, "$input", true);
            const success = 0 === errors.length;
            return success ? {
                success,
                data: input
            } : {
                success,
                errors,
                data: input
            } as any;
        }
        return {
            success: true,
            data: input
        } as any;
    }; })()(context);
    if (!contextResult.success) {
        reportRejection(`Invalid context: ${toSchemaProblems(contextResult.errors).join('; ')}`, { schemaErrors: contextResult.errors });
    }
    return contextResult.success;
};
export const automapColumnDefinition = defineAiControlledCommand<ResolvedPayload, AutomapColumnsContext>({
    name: AiControlledCommandId.AUTO_MAP_COLUMNS_FOR_IMPORTS,
    domain: INTEGRATIONS_DOMAIN,
    requiresAppShell: true,
    description: 'Set up the tables of the import (the sheets, for an Excel file): whether rows are components or references, the workspace(s) to import into, and the column mappings. Use when the user asks to automap.',
    contextDescription: "'tables' are the tables of the import (the sheets, for an Excel file); 'columns' are a table's column names and the index is the column position. 'sampleRows' are the table's first rows, one value per column in 'columns' order; use them to tell names from IDs, paths and OIDs. 'rowRepresentation' says whether each row becomes a component ('components') or a reference ('references'), which decides the column types you can use. 'workspaces' are the existing workspaces you can import into, with the component and reference types each one already has; 'canCreateWorkspace' says whether you may propose a new one. 'canCreateTypes' says whether you may propose new component types, reference types and fields. 'fields' are the existing fields a column can map to; copy 'name', 'label' and 'type' from one entry exactly. When a table has 'lockedRowRepresentation', use it; map every column in 'lockedColumns' as the column type it gives. In a component sheet, we should always try to map component if available. Then if custom-id if component is not available and if they're both not available, we should try to map component-path or ardoq-oid or component-key",
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                AutomapColumnsPayload: {
                    type: "object",
                    properties: {
                        tables: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/AutomapTable"
                            },
                            description: "One entry per table to set up. Leave out tables that hold no components or references, such as change logs, read-me or instruction sheets. Tables left out are not changed."
                        }
                    },
                    required: [
                        "tables"
                    ]
                },
                AutomapTable: {
                    type: "object",
                    properties: {
                        tableId: {
                            type: "string",
                            description: "'id' of an entry in 'tables'."
                        },
                        rowRepresentation: {
                            $ref: "#/components/schemas/TableMappingType",
                            description: "Whether each row of the sheet becomes a component or a reference."
                        },
                        rootWorkspace: {
                            $ref: "#/components/schemas/IntegrationWorkspace",
                            description: "Components: the workspace the components go in. References: the workspace of the source components. Copy an entry from 'workspaces', or use { id: null, name } for a new workspace."
                        },
                        targetWorkspace: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    $ref: "#/components/schemas/IntegrationWorkspace"
                                }
                            ],
                            description: "References only: the workspace of the target components, in the same form as rootWorkspace. null for components; for references, null means the same as rootWorkspace."
                        },
                        mappings: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/AutomapColumnMapping"
                            },
                            description: "One entry per column to map. Leave a column out when nothing fits."
                        }
                    },
                    required: [
                        "tableId",
                        "rowRepresentation",
                        "rootWorkspace",
                        "targetWorkspace",
                        "mappings"
                    ]
                },
                TableMappingType: {
                    oneOf: [
                        {
                            "const": "components"
                        },
                        {
                            "const": "references"
                        }
                    ]
                },
                IntegrationWorkspace: {
                    type: "object",
                    properties: {
                        id: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ]
                        },
                        name: {
                            type: "string"
                        }
                    },
                    required: [
                        "id",
                        "name"
                    ]
                },
                AutomapColumnMapping: {
                    type: "object",
                    properties: {
                        columnName: {
                            type: "string",
                            description: "Name of the column in the sheet."
                        },
                        columnIndex: {
                            type: "number",
                            description: "Position of the column in 'columns'."
                        },
                        mappedTo: {
                            $ref: "#/components/schemas/AutomapMappedTo"
                        }
                    },
                    required: [
                        "columnName",
                        "columnIndex",
                        "mappedTo"
                    ]
                },
                AutomapMappedTo: {
                    oneOf: [
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "component",
                                    description: "The column holds component names. Prefer it for identifying a components sheet's rows. Headers like 'X level 1', 'level_2', 'level_3' are successive hierarchy levels: map each as 'component' with hierarchyLevel 1, 2, 3."
                                },
                                componentType: {
                                    oneOf: [
                                        {
                                            type: "null"
                                        },
                                        {
                                            type: "string"
                                        }
                                    ],
                                    description: "A name from the root workspace's 'componentTypes', or a new name when 'canCreateTypes' is true. When one type fits the whole sheet (e.g. sheet 'Applications' and type 'Application'), set it here and leave any Type column unmapped. null only when the sheet mixes types and maps a 'component-type' column."
                                },
                                hierarchyLevel: {
                                    type: "number",
                                    description: "1 for the top level, 2 for its children, and so on, as in headers like 'level_1', 'level_2'. Levels on a sheet start at 1 with no gaps."
                                }
                            },
                            required: [
                                "columnType",
                                "componentType",
                                "hierarchyLevel"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "component-path",
                                    description: "The column holds a full component path, e.g. \"Parent::Child\"."
                                },
                                componentTypes: {
                                    type: "array",
                                    items: {
                                        type: "string"
                                    },
                                    description: "One type per path level, top level first, chosen like 'componentType' on a 'component' column."
                                }
                            },
                            required: [
                                "columnType",
                                "componentTypes"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "component-type",
                                    description: "The column holds each row's component type name, e.g. a 'Type' column. Only map it when the sheet mixes component types. Never use it for component names."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "field",
                                    description: "The column holds values for a field."
                                },
                                existingField: {
                                    oneOf: [
                                        {
                                            type: "null"
                                        },
                                        {
                                            $ref: "#/components/schemas/ContextField"
                                        }
                                    ],
                                    description: "An entry copied exactly from 'fields' when its label or name matches the column (e.g. 'description' -> 'Description'), or null to create a new field named after the column."
                                }
                            },
                            required: [
                                "columnType",
                                "existingField"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "custom-id",
                                    description: "The column holds stable unique IDs (not names) that identify each row's component or reference across imports. Never use it for component names."
                                },
                                existingField: {
                                    oneOf: [
                                        {
                                            type: "null"
                                        },
                                        {
                                            $ref: "#/components/schemas/ContextField"
                                        }
                                    ],
                                    description: "The field that stores the ID: an entry copied exactly from 'fields', or null to create a new field named after the column."
                                }
                            },
                            required: [
                                "columnType",
                                "existingField"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "ardoq-oid",
                                    description: "The column holds Ardoq OIDs."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "component-key",
                                    description: "The column holds Ardoq IDs (component keys)."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "description",
                                    description: "The column holds descriptions. Only use it when 'fields' has no field matching the column; otherwise map it as that field."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "display-text",
                                    description: "The column holds the reference display text."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "tags",
                                    description: "The column holds tags."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "parent",
                                    description: "The column identifies each component's parent."
                                },
                                parentFormat: {
                                    $ref: "#/components/schemas/AiParentFormat",
                                    description: "How the parent is identified: its path, name, Ardoq OID or Ardoq ID."
                                }
                            },
                            required: [
                                "columnType",
                                "parentFormat"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "reference",
                                    description: "The column identifies components that each row's component references. Headers like 'has_successor (applications)' name the reference type and, in brackets, the workspace of the other end."
                                },
                                referenceFormat: {
                                    $ref: "#/components/schemas/AiReferenceFormat",
                                    description: "How the referenced component is identified. Use 'path' unless the values are Ardoq OIDs or Ardoq IDs."
                                },
                                referenceType: {
                                    type: "string",
                                    description: "Name of the reference type. Prefer one of the workspace's 'referenceTypes'."
                                },
                                referenceDirection: {
                                    $ref: "#/components/schemas/IntegrationReferenceDirection",
                                    description: "'outgoing': the column holds the targets of references from each row's component. 'incoming': the column holds the sources of references to each row's component."
                                },
                                targetWorkspace: {
                                    $ref: "#/components/schemas/IntegrationWorkspace",
                                    description: "The workspace of the components the column identifies: the targets for 'outgoing', the sources for 'incoming'. Copy an entry from 'workspaces', or use { id: null, name } for a new workspace."
                                },
                                componentType: {
                                    type: "string",
                                    description: "Type of the components this import creates when a component the column identifies is missing, from 'targetWorkspace': a name from that workspace's 'componentTypes', or a new name when 'canCreateTypes' is true."
                                }
                            },
                            required: [
                                "columnType",
                                "referenceFormat",
                                "referenceType",
                                "referenceDirection",
                                "targetWorkspace",
                                "componentType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "root-reference",
                                    description: "The column identifies the source component of each reference."
                                },
                                referenceFormat: {
                                    $ref: "#/components/schemas/AiReferenceFormat",
                                    description: "Use 'path' unless the values are Ardoq OIDs or Ardoq IDs."
                                },
                                referenceType: {
                                    oneOf: [
                                        {
                                            type: "null"
                                        },
                                        {
                                            type: "string"
                                        }
                                    ],
                                    description: "Name of the reference type. Prefer one of the root workspace's 'referenceTypes'. null only when the sheet also has a 'reference-type' column."
                                },
                                componentType: {
                                    type: "string",
                                    description: "Type of the components this import creates when a source component is missing, from 'rootWorkspace': a name from that workspace's 'componentTypes', or a new name when 'canCreateTypes' is true."
                                }
                            },
                            required: [
                                "columnType",
                                "referenceFormat",
                                "referenceType",
                                "componentType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "target-reference",
                                    description: "The column identifies the target component of each reference."
                                },
                                referenceFormat: {
                                    $ref: "#/components/schemas/AiReferenceFormat",
                                    description: "Use 'path' unless the values are Ardoq OIDs or Ardoq IDs."
                                },
                                componentType: {
                                    type: "string",
                                    description: "Type of the components this import creates when a target component is missing, from the sheet's 'targetWorkspace': a name from that workspace's 'componentTypes', or a new name when 'canCreateTypes' is true."
                                }
                            },
                            required: [
                                "columnType",
                                "referenceFormat",
                                "componentType"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                columnType: {
                                    "const": "reference-type",
                                    description: "The column holds the reference type name per row."
                                }
                            },
                            required: [
                                "columnType"
                            ]
                        }
                    ]
                },
                ContextField: {
                    type: "object",
                    properties: {
                        name: {
                            type: "string"
                        },
                        label: {
                            type: "string"
                        },
                        type: {
                            type: "string"
                        }
                    },
                    required: [
                        "name",
                        "label",
                        "type"
                    ]
                },
                AiParentFormat: {
                    oneOf: [
                        {
                            "const": "ardoq-oid"
                        },
                        {
                            "const": "component-key"
                        },
                        {
                            "const": "path"
                        },
                        {
                            "const": "name"
                        }
                    ]
                },
                AiReferenceFormat: {
                    oneOf: [
                        {
                            "const": "ardoq-oid"
                        },
                        {
                            "const": "component-key"
                        },
                        {
                            "const": "path"
                        },
                        {
                            "const": "name"
                        }
                    ]
                },
                IntegrationReferenceDirection: {
                    oneOf: [
                        {
                            "const": "outgoing"
                        },
                        {
                            "const": "incoming"
                        }
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/AutomapColumnsPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    isContext: isAutomapColumnsContext,
    validate: (raw, context) => {
        const payloadResult = (() => { const _io0 = (input: any): boolean => Array.isArray(input.tables) && input.tables.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)); const _io1 = (input: any): boolean => "string" === typeof input.tableId && ("components" === input.rowRepresentation || "references" === input.rowRepresentation) && ("object" === typeof input.rootWorkspace && null !== input.rootWorkspace && _io2(input.rootWorkspace)) && (null === input.targetWorkspace || "object" === typeof input.targetWorkspace && null !== input.targetWorkspace && _io2(input.targetWorkspace)) && (Array.isArray(input.mappings) && input.mappings.every((elem: any) => "object" === typeof elem && null !== elem && _io3(elem))); const _io2 = (input: any): boolean => (null === input.id || "string" === typeof input.id) && "string" === typeof input.name; const _io3 = (input: any): boolean => "string" === typeof input.columnName && "number" === typeof input.columnIndex && ("object" === typeof input.mappedTo && null !== input.mappedTo && _iu0(input.mappedTo)); const _io4 = (input: any): boolean => "component" === input.columnType && (null === input.componentType || "string" === typeof input.componentType) && "number" === typeof input.hierarchyLevel; const _io5 = (input: any): boolean => "component-path" === input.columnType && (Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem)); const _io6 = (input: any): boolean => "component-type" === input.columnType; const _io7 = (input: any): boolean => "field" === input.columnType && (null === input.existingField || "object" === typeof input.existingField && null !== input.existingField && _io8(input.existingField)); const _io8 = (input: any): boolean => "string" === typeof input.name && "string" === typeof input.label && "string" === typeof input.type; const _io9 = (input: any): boolean => "custom-id" === input.columnType && (null === input.existingField || "object" === typeof input.existingField && null !== input.existingField && _io8(input.existingField)); const _io10 = (input: any): boolean => "ardoq-oid" === input.columnType; const _io11 = (input: any): boolean => "component-key" === input.columnType; const _io12 = (input: any): boolean => "description" === input.columnType; const _io13 = (input: any): boolean => "display-text" === input.columnType; const _io14 = (input: any): boolean => "tags" === input.columnType; const _io15 = (input: any): boolean => "parent" === input.columnType && ("ardoq-oid" === input.parentFormat || "component-key" === input.parentFormat || "path" === input.parentFormat || "name" === input.parentFormat); const _io16 = (input: any): boolean => "reference" === input.columnType && ("ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat) && "string" === typeof input.referenceType && ("outgoing" === input.referenceDirection || "incoming" === input.referenceDirection) && ("object" === typeof input.targetWorkspace && null !== input.targetWorkspace && _io2(input.targetWorkspace)) && "string" === typeof input.componentType; const _io17 = (input: any): boolean => "root-reference" === input.columnType && ("ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat) && (null === input.referenceType || "string" === typeof input.referenceType) && "string" === typeof input.componentType; const _io18 = (input: any): boolean => "target-reference" === input.columnType && ("ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat) && "string" === typeof input.componentType; const _io19 = (input: any): boolean => "reference-type" === input.columnType; const _iu0 = (input: any): any => (() => {
            if ("component" === input.columnType)
                return _io4(input);
            else if ("component-path" === input.columnType)
                return _io5(input);
            else if ("reference-type" === input.columnType)
                return _io19(input);
            else if ("tags" === input.columnType)
                return _io14(input);
            else if ("display-text" === input.columnType)
                return _io13(input);
            else if ("description" === input.columnType)
                return _io12(input);
            else if ("component-key" === input.columnType)
                return _io11(input);
            else if ("ardoq-oid" === input.columnType)
                return _io10(input);
            else if ("component-type" === input.columnType)
                return _io6(input);
            else if ("custom-id" === input.columnType)
                return _io9(input);
            else if ("field" === input.columnType)
                return _io7(input);
            else if ("parent" === input.columnType)
                return _io15(input);
            else if ("reference" === input.columnType)
                return _io16(input);
            else if ("root-reference" === input.columnType)
                return _io17(input);
            else if ("target-reference" === input.columnType)
                return _io18(input);
            else
                return false;
        })(); const _vo0 = (input: any, _path: string, _exceptionable: boolean = true): boolean => [(Array.isArray(input.tables) || _report(_exceptionable, {
                path: _path + ".tables",
                expected: "Array<AutomapTable>",
                value: input.tables
            })) && input.tables.map((elem: any, _index4: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
                path: _path + ".tables[" + _index4 + "]",
                expected: "AutomapTable",
                value: elem
            })) && _vo1(elem, _path + ".tables[" + _index4 + "]", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".tables[" + _index4 + "]",
                expected: "AutomapTable",
                value: elem
            })).every((flag: boolean) => flag) || _report(_exceptionable, {
                path: _path + ".tables",
                expected: "Array<AutomapTable>",
                value: input.tables
            })].every((flag: boolean) => flag); const _vo1 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.tableId || _report(_exceptionable, {
                path: _path + ".tableId",
                expected: "string",
                value: input.tableId
            }), "components" === input.rowRepresentation || "references" === input.rowRepresentation || _report(_exceptionable, {
                path: _path + ".rowRepresentation",
                expected: "(\"components\" | \"references\")",
                value: input.rowRepresentation
            }), ("object" === typeof input.rootWorkspace && null !== input.rootWorkspace || _report(_exceptionable, {
                path: _path + ".rootWorkspace",
                expected: "IntegrationWorkspace",
                value: input.rootWorkspace
            })) && _vo2(input.rootWorkspace, _path + ".rootWorkspace", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".rootWorkspace",
                expected: "IntegrationWorkspace",
                value: input.rootWorkspace
            }), null === input.targetWorkspace || ("object" === typeof input.targetWorkspace && null !== input.targetWorkspace || _report(_exceptionable, {
                path: _path + ".targetWorkspace",
                expected: "(IntegrationWorkspace | null)",
                value: input.targetWorkspace
            })) && _vo2(input.targetWorkspace, _path + ".targetWorkspace", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".targetWorkspace",
                expected: "(IntegrationWorkspace | null)",
                value: input.targetWorkspace
            }), (Array.isArray(input.mappings) || _report(_exceptionable, {
                path: _path + ".mappings",
                expected: "Array<AutomapColumnMapping>",
                value: input.mappings
            })) && input.mappings.map((elem: any, _index5: number) => ("object" === typeof elem && null !== elem || _report(_exceptionable, {
                path: _path + ".mappings[" + _index5 + "]",
                expected: "AutomapColumnMapping",
                value: elem
            })) && _vo3(elem, _path + ".mappings[" + _index5 + "]", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".mappings[" + _index5 + "]",
                expected: "AutomapColumnMapping",
                value: elem
            })).every((flag: boolean) => flag) || _report(_exceptionable, {
                path: _path + ".mappings",
                expected: "Array<AutomapColumnMapping>",
                value: input.mappings
            })].every((flag: boolean) => flag); const _vo2 = (input: any, _path: string, _exceptionable: boolean = true): boolean => [null === input.id || "string" === typeof input.id || _report(_exceptionable, {
                path: _path + ".id",
                expected: "(null | string)",
                value: input.id
            }), "string" === typeof input.name || _report(_exceptionable, {
                path: _path + ".name",
                expected: "string",
                value: input.name
            })].every((flag: boolean) => flag); const _vo3 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.columnName || _report(_exceptionable, {
                path: _path + ".columnName",
                expected: "string",
                value: input.columnName
            }), "number" === typeof input.columnIndex || _report(_exceptionable, {
                path: _path + ".columnIndex",
                expected: "number",
                value: input.columnIndex
            }), ("object" === typeof input.mappedTo && null !== input.mappedTo || _report(_exceptionable, {
                path: _path + ".mappedTo",
                expected: "(__type | __type.o1 | __type.o10 | __type.o11 | __type.o12 | __type.o13 | __type.o14 | __type.o2 | __type.o3 | __type.o4 | __type.o5 | __type.o6 | __type.o7 | __type.o8 | __type.o9)",
                value: input.mappedTo
            })) && _vu0(input.mappedTo, _path + ".mappedTo", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".mappedTo",
                expected: "(__type | __type.o1 | __type.o10 | __type.o11 | __type.o12 | __type.o13 | __type.o14 | __type.o2 | __type.o3 | __type.o4 | __type.o5 | __type.o6 | __type.o7 | __type.o8 | __type.o9)",
                value: input.mappedTo
            })].every((flag: boolean) => flag); const _vo4 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["component" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"component\"",
                value: input.columnType
            }), null === input.componentType || "string" === typeof input.componentType || _report(_exceptionable, {
                path: _path + ".componentType",
                expected: "(null | string)",
                value: input.componentType
            }), "number" === typeof input.hierarchyLevel || _report(_exceptionable, {
                path: _path + ".hierarchyLevel",
                expected: "number",
                value: input.hierarchyLevel
            })].every((flag: boolean) => flag); const _vo5 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["component-path" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"component-path\"",
                value: input.columnType
            }), (Array.isArray(input.componentTypes) || _report(_exceptionable, {
                path: _path + ".componentTypes",
                expected: "Array<string>",
                value: input.componentTypes
            })) && input.componentTypes.map((elem: any, _index6: number) => "string" === typeof elem || _report(_exceptionable, {
                path: _path + ".componentTypes[" + _index6 + "]",
                expected: "string",
                value: elem
            })).every((flag: boolean) => flag) || _report(_exceptionable, {
                path: _path + ".componentTypes",
                expected: "Array<string>",
                value: input.componentTypes
            })].every((flag: boolean) => flag); const _vo6 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["component-type" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"component-type\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo7 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["field" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"field\"",
                value: input.columnType
            }), null === input.existingField || ("object" === typeof input.existingField && null !== input.existingField || _report(_exceptionable, {
                path: _path + ".existingField",
                expected: "(ContextField | null)",
                value: input.existingField
            })) && _vo8(input.existingField, _path + ".existingField", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".existingField",
                expected: "(ContextField | null)",
                value: input.existingField
            })].every((flag: boolean) => flag); const _vo8 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["string" === typeof input.name || _report(_exceptionable, {
                path: _path + ".name",
                expected: "string",
                value: input.name
            }), "string" === typeof input.label || _report(_exceptionable, {
                path: _path + ".label",
                expected: "string",
                value: input.label
            }), "string" === typeof input.type || _report(_exceptionable, {
                path: _path + ".type",
                expected: "string",
                value: input.type
            })].every((flag: boolean) => flag); const _vo9 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["custom-id" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"custom-id\"",
                value: input.columnType
            }), null === input.existingField || ("object" === typeof input.existingField && null !== input.existingField || _report(_exceptionable, {
                path: _path + ".existingField",
                expected: "(ContextField | null)",
                value: input.existingField
            })) && _vo8(input.existingField, _path + ".existingField", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".existingField",
                expected: "(ContextField | null)",
                value: input.existingField
            })].every((flag: boolean) => flag); const _vo10 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["ardoq-oid" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"ardoq-oid\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo11 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["component-key" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"component-key\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo12 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["description" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"description\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo13 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["display-text" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"display-text\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo14 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["tags" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"tags\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vo15 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["parent" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"parent\"",
                value: input.columnType
            }), "ardoq-oid" === input.parentFormat || "component-key" === input.parentFormat || "path" === input.parentFormat || "name" === input.parentFormat || _report(_exceptionable, {
                path: _path + ".parentFormat",
                expected: "(\"ardoq-oid\" | \"component-key\" | \"name\" | \"path\")",
                value: input.parentFormat
            })].every((flag: boolean) => flag); const _vo16 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["reference" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"reference\"",
                value: input.columnType
            }), "ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat || _report(_exceptionable, {
                path: _path + ".referenceFormat",
                expected: "(\"ardoq-oid\" | \"component-key\" | \"name\" | \"path\")",
                value: input.referenceFormat
            }), "string" === typeof input.referenceType || _report(_exceptionable, {
                path: _path + ".referenceType",
                expected: "string",
                value: input.referenceType
            }), "outgoing" === input.referenceDirection || "incoming" === input.referenceDirection || _report(_exceptionable, {
                path: _path + ".referenceDirection",
                expected: "(\"incoming\" | \"outgoing\")",
                value: input.referenceDirection
            }), ("object" === typeof input.targetWorkspace && null !== input.targetWorkspace || _report(_exceptionable, {
                path: _path + ".targetWorkspace",
                expected: "IntegrationWorkspace",
                value: input.targetWorkspace
            })) && _vo2(input.targetWorkspace, _path + ".targetWorkspace", true && _exceptionable) || _report(_exceptionable, {
                path: _path + ".targetWorkspace",
                expected: "IntegrationWorkspace",
                value: input.targetWorkspace
            }), "string" === typeof input.componentType || _report(_exceptionable, {
                path: _path + ".componentType",
                expected: "string",
                value: input.componentType
            })].every((flag: boolean) => flag); const _vo17 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["root-reference" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"root-reference\"",
                value: input.columnType
            }), "ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat || _report(_exceptionable, {
                path: _path + ".referenceFormat",
                expected: "(\"ardoq-oid\" | \"component-key\" | \"name\" | \"path\")",
                value: input.referenceFormat
            }), null === input.referenceType || "string" === typeof input.referenceType || _report(_exceptionable, {
                path: _path + ".referenceType",
                expected: "(null | string)",
                value: input.referenceType
            }), "string" === typeof input.componentType || _report(_exceptionable, {
                path: _path + ".componentType",
                expected: "string",
                value: input.componentType
            })].every((flag: boolean) => flag); const _vo18 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["target-reference" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"target-reference\"",
                value: input.columnType
            }), "ardoq-oid" === input.referenceFormat || "component-key" === input.referenceFormat || "path" === input.referenceFormat || "name" === input.referenceFormat || _report(_exceptionable, {
                path: _path + ".referenceFormat",
                expected: "(\"ardoq-oid\" | \"component-key\" | \"name\" | \"path\")",
                value: input.referenceFormat
            }), "string" === typeof input.componentType || _report(_exceptionable, {
                path: _path + ".componentType",
                expected: "string",
                value: input.componentType
            })].every((flag: boolean) => flag); const _vo19 = (input: any, _path: string, _exceptionable: boolean = true): boolean => ["reference-type" === input.columnType || _report(_exceptionable, {
                path: _path + ".columnType",
                expected: "\"reference-type\"",
                value: input.columnType
            })].every((flag: boolean) => flag); const _vu0 = (input: any, _path: string, _exceptionable: boolean = true): any => (() => {
            if ("component" === input.columnType)
                return _vo4(input, _path, true && _exceptionable);
            else if ("component-path" === input.columnType)
                return _vo5(input, _path, true && _exceptionable);
            else if ("reference-type" === input.columnType)
                return _vo19(input, _path, true && _exceptionable);
            else if ("tags" === input.columnType)
                return _vo14(input, _path, true && _exceptionable);
            else if ("display-text" === input.columnType)
                return _vo13(input, _path, true && _exceptionable);
            else if ("description" === input.columnType)
                return _vo12(input, _path, true && _exceptionable);
            else if ("component-key" === input.columnType)
                return _vo11(input, _path, true && _exceptionable);
            else if ("ardoq-oid" === input.columnType)
                return _vo10(input, _path, true && _exceptionable);
            else if ("component-type" === input.columnType)
                return _vo6(input, _path, true && _exceptionable);
            else if ("custom-id" === input.columnType)
                return _vo9(input, _path, true && _exceptionable);
            else if ("field" === input.columnType)
                return _vo7(input, _path, true && _exceptionable);
            else if ("parent" === input.columnType)
                return _vo15(input, _path, true && _exceptionable);
            else if ("reference" === input.columnType)
                return _vo16(input, _path, true && _exceptionable);
            else if ("root-reference" === input.columnType)
                return _vo17(input, _path, true && _exceptionable);
            else if ("target-reference" === input.columnType)
                return _vo18(input, _path, true && _exceptionable);
            else
                return _report(_exceptionable, {
                    path: _path,
                    expected: "(__type | __type.o1 | __type.o14 | __type.o9 | __type.o8 | __type.o7 | __type.o6 | __type.o5 | __type.o2 | __type.o4 | __type.o3 | __type.o10 | __type.o11 | __type.o12 | __type.o13)",
                    value: input
                });
        })(); const __is = (input: any): input is AutomapColumnsPayload => "object" === typeof input && null !== input && _io0(input); let errors: any; let _report: any; return (input: any): import("typia").IValidation<AutomapColumnsPayload> => {
            if (false === __is(input)) {
                errors = [];
                _report = (__typia_transform__validateReport._validateReport as any)(errors);
                ((input: any, _path: string, _exceptionable: boolean = true) => ("object" === typeof input && null !== input || _report(true, {
                    path: _path + "",
                    expected: "AutomapColumnsPayload",
                    value: input
                })) && _vo0(input, _path + "", true) || _report(true, {
                    path: _path + "",
                    expected: "AutomapColumnsPayload",
                    value: input
                }))(input, "$input", true);
                const success = 0 === errors.length;
                return success ? {
                    success,
                    data: input
                } : {
                    success,
                    errors,
                    data: input
                } as any;
            }
            return {
                success: true,
                data: input
            } as any;
        }; })()(raw);
        if (!payloadResult.success) {
            const reason = [
                'Invalid mapping payload:',
                ...toSchemaProblems(payloadResult.errors).map(problem => `- ${problem}`),
            ].join('\n');
            return reportRejection(reason, {
                schemaErrors: payloadResult.errors,
                payload: raw,
            });
        }
        const resolution = resolvePayload(payloadResult.data, context);
        if (resolution.status === 'rejected') {
            return reportRejection(resolution.reason, {
                payload: payloadResult.data,
            });
        }
        return resolution;
    },
    execute: ({ integrationId, tables }) => tables
        .flatMap(table => toTableActions(integrationId, table))
        .forEach(action => dispatchAction(action)),
    isApplied: () => true, // POC: replace with a real check against the mapping state
});
export const buildAutomapColumnsCommand = (context: AutomapColumnsContext): AiControlledCommand<typeof automapColumnDefinition> => ({
    ...automapColumnDefinition,
    context,
});
